import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../contexts/ThemeContext';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import { validateRequired } from '../utils/validators';
import { ReactComponent as SunIcon } from '../assets/icons/bulb.svg';
import { ReactComponent as MoonIcon } from '../assets/icons/bulb-on.svg';
import logo from '../assets/bion-logo.png';

const Login = () => {
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    setErrorMessage('');
  };

  const validate = () => {
    const newErrors = {};
    
    if (!validateRequired(formData.username)) {
      newErrors.username = 'Username is required';
    }
    
    if (!validateRequired(formData.password)) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    
    if (!validate()) return;
    
    setLoading(true);
    
    try {
      const result = await login(formData.username, formData.password);
      
      if (!result.success) {
        setErrorMessage(result.error || 'Login failed. Please try again.');
      }
    } catch (error) {
      setErrorMessage('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Styles
  const containerStyle = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--background)',
    padding: '20px',
    position: 'relative'
  };

  const cardStyle = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '16px',
    padding: '40px',
    width: '100%',
    maxWidth: '450px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '32px'
  };

  const logoStyle = {
    maxHeight: "52px",
    marginBottom: "24px"
  };

  const titleStyle = {
    fontSize: '28px',
    fontWeight: '700',
    color: 'var(--text-primary)',
    marginBottom: '8px'
  };

  const subtitleStyle = {
    fontSize: '14px',
    color: 'var(--text-secondary)'
  };

  const errorBoxStyle = {
    background: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid #ef4444',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '20px',
    color: '#ef4444',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const themeSwitchStyle = {
    position: 'absolute',
    top: '20px',
    right: '20px',
    width: '44px',
    height: '44px',
    padding: '0',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '50%',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const buttonStyle = {
    marginTop: '16px !important',
  }

  const iconStyle = {
    width: '20px',
    height: '20px',
    color: 'var(--text-primary)',
    transition: 'transform 0.3s ease'
  };

  // Icons
  const userIcon = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  );

  const lockIcon = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );

  const alertIcon = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  );

  return (
    <div style={containerStyle}>
      <button
        style={themeSwitchStyle}
        onClick={toggleTheme}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.background = 'var(--surface-light)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.background = 'var(--surface)';
        }}
      >
        
        {theme === 'dark' ? (
          <SunIcon style={iconStyle} />
        ) : (
          <MoonIcon style={iconStyle} />
        )}
      </button>

      <div style={cardStyle}>
        <div style={headerStyle}>
          <div><img src={logo} alt="BION Logo" style={logoStyle} /></div>
          <h1 style={titleStyle}>AMC Ticketing System</h1>
          <p style={subtitleStyle}>Sign in to your account</p>
        </div>

        {errorMessage && (
          <div style={errorBoxStyle}>
            {alertIcon}
            <span>{errorMessage}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} style={{display: "flex", flexDirection: "column", gap:"16px"}}>
          <Input
            label="Username"
            name="username"
            type="text"
            placeholder="Enter your username"
            value={formData.username}
            onChange={handleChange}
            error={errors.username}
            icon={userIcon}
            required
            autoComplete="username"
          />

          <Input
            label="Password"
            name="password"
            type="password"
            canPeekPassword
            placeholder="Enter your password"
            value={formData.password}
            onChange={handleChange}
            error={errors.password}
            icon={lockIcon}
            required
            autoComplete="current-password"
          />

          <Button
            type="submit"
            variant="primary"
            fullWidth
            disabled={loading}
            style={buttonStyle}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

      </div>
    </div>
  );
};

export default Login;